import { Group, ScrollArea, Stack, Textarea, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STANDARD_DATE_FORMATS } from '../../../../../helpers/date-time-helpers';
import { baseWhite, inkLight, redBase } from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Divider } from '../../../../../ui-library/divider/divider';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import { commonInputProps } from './milestone-modal.helpers';
import type { MilestoneModalMode } from './milestone-modal.type';

type Props = {
  mode: MilestoneModalMode;
  form: UseFormReturnType<any>;
  onNext: (data: any) => void;
  onCancel: () => void;
  onDelete?: (...args: any[]) => Promise<void>;
  isDeleting?: boolean;
};

export function MilestoneModalStepOne({ mode, form, onNext, onCancel, onDelete, isDeleting = false }: Props) {
  const i18n = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDeleteConfirm = async () => {
    await onDelete?.();
    setIsDeleteModalOpen(false);
  };

  const handleNext = () => {
    form.validate();
    if (form.errors.name || form.errors.target_end_date) {
      return;
    }
    onNext(form.values);
  };

  return (
    <ScrollArea type="always" scrollbarSize={8}>
      <Stack gap={16} p={0} pr={16}>
        <Text>
          {i18n.t('strategy.milestones.form.description', {
            defaultValue: 'Define the milestone details and timeline',
          })}
        </Text>

        <Stack gap={16}>
          <TextInput
            required
            label={i18n.t('strategy.milestones.form.name_label', { defaultValue: 'Milestone Name' })}
            placeholder={i18n.t('strategy.milestones.form.name_placeholder', {
              defaultValue: 'Enter milestone name',
            })}
            {...commonInputProps}
            {...form.getInputProps('name')}
            error={form.errors.name ? 'Milestone name is required' : undefined}
          />

          <Textarea
            label={i18n.t('strategy.milestones.form.description_label', { defaultValue: 'Description' })}
            placeholder={i18n.t('strategy.milestones.form.description_placeholder', {
              defaultValue: 'Enter milestone description (optional)',
            })}
            {...commonInputProps}
            minRows={3}
            maxRows={5}
            {...form.getInputProps('description')}
          />

          <DatePickerInput
            required
            label={i18n.t('strategy.milestones.form.target_end_date_label', { defaultValue: 'Target End Date' })}
            placeholder={i18n.t('strategy.milestones.form.date_placeholder', { defaultValue: 'Select date' })}
            valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
            {...commonInputProps}
            {...form.getInputProps('target_end_date')}
            error={form.errors.target_end_date ? 'Target end date is required' : undefined}
            popoverProps={{ zIndex: 1000, withinPortal: true, position: 'top' }}
          />
        </Stack>

        <Divider />

        <Group justify="space-between">
          {mode === 'edit' && onDelete && (
            <Button
              radius="xl"
              variant="outline"
              onClick={() => setIsDeleteModalOpen(true)}
              leftSection={<Icon name="delete" color={inkLight} size={18} pointer />}
              style={{ borderColor: redBase }}
              disabled={isDeleting}
            >
              {i18n.t('strategy.milestones.form.delete_button', { defaultValue: 'Delete Milestone' })}
            </Button>
          )}
          <Group justify="flex-end" gap={16} flex={1}>
            <Button radius="xl" variant="outline" onClick={onCancel}>
              {i18n.t('strategy.milestones.form.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              radius="xl"
              variant="secondary"
              onClick={handleNext}
              rightSection={<Icon name="arrow_forward" color={baseWhite} size={16} pointer />}
            >
              {i18n.t('strategy.milestones.form.next_step', { defaultValue: 'Next' })}
            </Button>
          </Group>
        </Group>
      </Stack>

      {isDeleteModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: baseWhite,
              padding: 24,
              borderRadius: 8,
              zIndex: 1001,
              maxWidth: 400,
            }}
          >
            <Text weight="bold">
              {i18n.t('strategy.milestones.form.delete_confirmation', { defaultValue: 'Delete Milestone?' })}
            </Text>
            <Text size="small" color={inkLight}>
              {i18n.t('strategy.milestones.form.delete_confirmation_message', {
                defaultValue: 'Are you sure you want to delete this milestone?',
              })}
            </Text>
            <Group justify="flex-end" gap={16} mt={24}>
              <Button radius="xl" variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                radius="xl"
                variant="outline"
                style={{ borderColor: redBase, color: redBase }}
                loading={isDeleting}
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </Group>
          </div>
        </div>
      )}
    </ScrollArea>
  );
}
